import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Handshake, Copy, Eye } from 'lucide-react';
import { getPartners, getPartner, createPartner, updatePartner } from '../services/api';
import './PartnersPage.css';

const TIERS = ['A', 'B', 'C'];
const STATUSES = ['active', 'paused', 'terminated'];
const CODE_RE = /^[A-Z0-9]{4,20}$/;

const EMPTY_FORM = {
  code: '',
  partnerName: '',
  partnerEmail: '',
  tier: 'A',
  status: 'active',
  commissionPct: '',
  commissionMonths: '',
  enterpriseFlat: '',
  signOnBonus: '',
  contractStart: '',
  contractEnd: '',
};

// Base pública de la landing v2 donde vive el route /con/{handle} (nerdo-web).
const LANDING_BASE = 'https://nerdo.solutions/resto/con';

function PartnersPage() {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [detail, setDetail] = useState(null);

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const response = await getPartners();
      setPartners(response.partners || []);
    } catch (error) {
      console.error('Error loading partners:', error);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const nf = useMemo(() => new Intl.NumberFormat('es-AR'), []);
  const formatArs = useCallback(
    (value) =>
      value === undefined || value === null || value === ''
        ? '—'
        : `$${nf.format(Number(value))}`,
    [nf]
  );
  // Métricas Phase 2 (commission calculator, NER-3081): se muestran si el backend
  // ya las expone en el objeto partner; si no, placeholder.
  const metric = useCallback((value, formatter) => {
    if (value === undefined || value === null || value === '') return '—';
    return formatter ? formatter(value) : value;
  }, []);

  const formatDate = useCallback((iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR');
  }, []);

  const handleOpenModal = (partner = null) => {
    if (partner) {
      setEditing(partner);
      setFormData({
        code: partner.code || '',
        partnerName: partner.partnerName || '',
        partnerEmail: partner.partnerEmail || '',
        tier: partner.tier || 'A',
        status: partner.status || 'active',
        commissionPct: partner.commissionPct ?? '',
        commissionMonths: partner.commissionMonths ?? '',
        enterpriseFlat: partner.enterpriseFlat ?? '',
        signOnBonus: partner.signOnBonus ?? '',
        contractStart: (partner.contractStart || '').slice(0, 10),
        contractEnd: (partner.contractEnd || '').slice(0, 10),
      });
    } else {
      setEditing(null);
      setFormData(EMPTY_FORM);
    }
    setFormErrors({});
    setModalOpen(true);
  };

  const buildPayload = () => {
    const payload = {
      partnerName: formData.partnerName.trim(),
      partnerEmail: formData.partnerEmail.trim(),
      tier: formData.tier,
    };
    // Numéricos: sólo enviar si el usuario cargó un valor (si se omiten, el
    // backend aplica sus defaults por tier).
    ['commissionPct', 'commissionMonths', 'enterpriseFlat', 'signOnBonus'].forEach((k) => {
      if (formData[k] !== '' && formData[k] !== null) payload[k] = Number(formData[k]);
    });
    if (formData.contractStart) payload.contractStart = new Date(formData.contractStart).toISOString();
    if (formData.contractEnd) payload.contractEnd = new Date(formData.contractEnd).toISOString();
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!editing) {
      const code = formData.code.trim().toUpperCase();
      if (!CODE_RE.test(code)) errors.code = t('partners.validation.code', 'Código: 4-20 alfanuméricos');
    }
    if (!formData.partnerName.trim()) errors.partnerName = t('partners.validation.name', 'Requerido');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.partnerEmail.trim())) {
      errors.partnerEmail = t('partners.validation.email', 'Email inválido');
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = buildPayload();
        payload.status = formData.status;
        await updatePartner(editing.code, payload);
      } else {
        const payload = buildPayload();
        payload.code = formData.code.trim().toUpperCase();
        await createPartner(payload);
      }
      setModalOpen(false);
      await loadPartners();
      showFeedback('success', t('partners.saveSuccess', 'Partner guardado'));
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        showFeedback('error', t('partners.duplicateError', 'Ya existe un partner con ese código'));
      } else {
        console.error('Error saving partner:', error);
        showFeedback('error', t('partners.saveError', 'Error al guardar el partner'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (partner, status) => {
    const label =
      status === 'terminated'
        ? t('partners.confirmTerminate', '¿Dar de baja definitiva este código? No se podrá reactivar el histórico de atribución.')
        : t('common.confirmAction', '¿Confirmás la acción?');
    if (status === 'terminated' && !window.confirm(label)) return;
    try {
      await updatePartner(partner.code, { status });
      await loadPartners();
      showFeedback('success', t('partners.statusUpdated', 'Estado actualizado'));
    } catch (error) {
      console.error('Error updating partner status:', error);
      showFeedback('error', t('partners.saveError', 'Error al guardar el partner'));
    }
  };

  const openDetail = async (partner) => {
    setDetail(partner); // mostrar de inmediato con datos de la lista
    try {
      const response = await getPartner(partner.code);
      if (response.partner) setDetail(response.partner);
    } catch (error) {
      console.error('Error loading partner detail:', error);
    }
  };

  const copyLink = (code) => {
    const url = `${LANDING_BASE}/${code}`;
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => showFeedback('success', t('partners.linkCopied', 'Link copiado')))
        .catch(() => showFeedback('error', t('partners.linkCopyError', 'No se pudo copiar')));
    }
  };

  const statusLabel = (status) => t(`partners.status.${status}`, status);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return partners;
    const q = searchQuery.toLowerCase();
    return partners.filter(
      (p) =>
        (p.code && p.code.toLowerCase().includes(q)) ||
        (p.partnerName && p.partnerName.toLowerCase().includes(q)) ||
        (p.tier && p.tier.toLowerCase() === q) ||
        (p.status && p.status.toLowerCase().includes(q))
    );
  }, [partners, searchQuery]);

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="partners-page">
      <div className="page-header">
        <h1>{t('partners.title', 'Partners')}</h1>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          {t('partners.addPartner', 'Nuevo código')}
        </button>
      </div>

      <div className="alert alert-warning partners-phase2-note" role="note">
        {t(
          'partners.phase2Note',
          'Las métricas de conversión y comisión se activan cuando el commission calculator (NER-3081) esté desplegado.'
        )}
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('partners.searchPlaceholder', 'Buscar por código, nombre, tier o estado…')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <Handshake size={48} />
            <p>{t('partners.emptyState', 'Todavía no hay códigos de partner')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="partners-table">
              <thead>
                <tr>
                  <th>{t('partners.code', 'Código')}</th>
                  <th>{t('partners.name', 'Nombre')}</th>
                  <th>{t('partners.tier', 'Tier')}</th>
                  <th>{t('partners.statusLabel', 'Estado')}</th>
                  <th>{t('partners.activeConversions', 'Conversiones')}</th>
                  <th>{t('partners.totalMRR', 'MRR')}</th>
                  <th>{t('partners.commissionAccrued', 'Comisión acum.')}</th>
                  <th>{t('partners.lastConversion', 'Última conversión')}</th>
                  <th>{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.code}>
                    <td data-label={t('partners.code', 'Código')}>
                      <code className="partner-code">{p.code}</code>
                    </td>
                    <td data-label={t('partners.name', 'Nombre')}>{p.partnerName}</td>
                    <td data-label={t('partners.tier', 'Tier')}>
                      <span className="tier-badge">{p.tier}</span>
                    </td>
                    <td data-label={t('partners.statusLabel', 'Estado')}>
                      <span className={`status-badge status-${p.status}`}>
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td data-label={t('partners.activeConversions', 'Conversiones')}>
                      {metric(p.activeConversions)}
                    </td>
                    <td data-label={t('partners.totalMRR', 'MRR')}>
                      {metric(p.totalMRR, formatArs)}
                    </td>
                    <td data-label={t('partners.commissionAccrued', 'Comisión acum.')}>
                      {metric(p.commissionAccrued, formatArs)}
                    </td>
                    <td data-label={t('partners.lastConversion', 'Última conversión')}>
                      {metric(p.lastConversion, formatDate)}
                    </td>
                    <td data-label={t('common.actions', 'Acciones')} className="partners-actions">
                      <button
                        type="button"
                        className="btn-icon"
                        title={t('partners.viewDetail', 'Ver detalle')}
                        onClick={() => openDetail(p)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        title={t('partners.copyLink', 'Copiar link de la landing')}
                        onClick={() => copyLink(p.code)}
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        title={t('common.edit', 'Editar')}
                        onClick={() => handleOpenModal(p)}
                      >
                        ✏️
                      </button>
                      {p.status === 'active' && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleStatusChange(p, 'paused')}
                        >
                          {t('partners.pause', 'Pausar')}
                        </button>
                      )}
                      {p.status === 'paused' && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleStatusChange(p, 'active')}
                        >
                          {t('partners.reactivate', 'Reactivar')}
                        </button>
                      )}
                      {p.status !== 'terminated' && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleStatusChange(p, 'terminated')}
                        >
                          {t('partners.terminate', 'Dar de baja')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {editing
                  ? t('partners.editPartner', 'Editar partner')
                  : t('partners.newPartner', 'Nuevo código de partner')}
              </h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setModalOpen(false)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body">
                <p className="required-legend">* {t('common.requiredFieldsLegend')}</p>
                <div className={`form-group${formErrors.code ? ' has-error' : ''}`}>
                  <label>{t('partners.code', 'Código')} *</label>
                  <input
                    type="text"
                    value={formData.code}
                    disabled={!!editing}
                    maxLength={20}
                    placeholder="NARDA20"
                    aria-required="true"
                    aria-invalid={!!formErrors.code}
                    onChange={(e) => {
                      if (formErrors.code) setFormErrors((prev) => ({ ...prev, code: '' }));
                      setFormData({ ...formData, code: e.target.value.toUpperCase() });
                    }}
                  />
                  {editing && (
                    <span className="field-hint">{t('partners.codeImmutable', 'El código no se puede modificar')}</span>
                  )}
                  {formErrors.code && (
                    <span className="field-error" role="alert">
                      {formErrors.code}
                    </span>
                  )}
                </div>
                <div className="form-row">
                  <div className={`form-group${formErrors.partnerName ? ' has-error' : ''}`}>
                    <label>{t('partners.name', 'Nombre')} *</label>
                    <input
                      type="text"
                      value={formData.partnerName}
                      aria-required="true"
                      aria-invalid={!!formErrors.partnerName}
                      onChange={(e) => {
                        if (formErrors.partnerName)
                          setFormErrors((prev) => ({ ...prev, partnerName: '' }));
                        setFormData({ ...formData, partnerName: e.target.value });
                      }}
                    />
                    {formErrors.partnerName && (
                      <span className="field-error" role="alert">
                        {formErrors.partnerName}
                      </span>
                    )}
                  </div>
                  <div className={`form-group${formErrors.partnerEmail ? ' has-error' : ''}`}>
                    <label>{t('partners.email', 'Email')} *</label>
                    <input
                      type="email"
                      value={formData.partnerEmail}
                      aria-required="true"
                      aria-invalid={!!formErrors.partnerEmail}
                      onChange={(e) => {
                        if (formErrors.partnerEmail)
                          setFormErrors((prev) => ({ ...prev, partnerEmail: '' }));
                        setFormData({ ...formData, partnerEmail: e.target.value });
                      }}
                    />
                    {formErrors.partnerEmail && (
                      <span className="field-error" role="alert">
                        {formErrors.partnerEmail}
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('partners.tier', 'Tier')} *</label>
                    <select
                      value={formData.tier}
                      onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                    >
                      {TIERS.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editing && (
                    <div className="form-group">
                      <label>{t('partners.statusLabel', 'Estado')}</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {statusLabel(s)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('partners.commissionPct', 'Comisión %')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.commissionPct}
                      placeholder="20"
                      onChange={(e) => setFormData({ ...formData, commissionPct: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('partners.commissionMonths', 'Meses de comisión')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.commissionMonths}
                      placeholder="12"
                      onChange={(e) => setFormData({ ...formData, commissionMonths: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('partners.enterpriseFlat', 'Flat Enterprise (ARS)')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.enterpriseFlat}
                      placeholder="500000"
                      onChange={(e) => setFormData({ ...formData, enterpriseFlat: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('partners.signOnBonus', 'Sign-on bonus (ARS)')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.signOnBonus}
                      placeholder={formData.tier === 'A' ? '50000' : '0'}
                      onChange={(e) => setFormData({ ...formData, signOnBonus: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('partners.contractStart', 'Inicio de contrato')}</label>
                    <input
                      type="date"
                      value={formData.contractStart}
                      onChange={(e) => setFormData({ ...formData, contractStart: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('partners.contractEnd', 'Fin de contrato')}</label>
                    <input
                      type="date"
                      value={formData.contractEnd}
                      onChange={(e) => setFormData({ ...formData, contractEnd: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.saving', 'Guardando…') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detail && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--wide">
            <div className="modal-header">
              <h2>
                {detail.partnerName} · <code className="partner-code">{detail.code}</code>
              </h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setDetail(null)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div>
                  <span className="detail-label">{t('partners.statusLabel', 'Estado')}</span>
                  <span className={`status-badge status-${detail.status}`}>
                    {statusLabel(detail.status)}
                  </span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.tier', 'Tier')}</span>
                  <span className="tier-badge">{detail.tier}</span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.email', 'Email')}</span>
                  <span>{detail.partnerEmail}</span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.commissionPct', 'Comisión %')}</span>
                  <span>
                    {detail.commissionPct}% × {detail.commissionMonths}{' '}
                    {t('partners.months', 'meses')}
                  </span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.enterpriseFlat', 'Flat Enterprise')}</span>
                  <span>{formatArs(detail.enterpriseFlat)}</span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.signOnBonus', 'Sign-on bonus')}</span>
                  <span>
                    {formatArs(detail.signOnBonus)} ({t('partners.signOnUsed', 'usados')}:{' '}
                    {detail.signOnUsed ?? 0}/3)
                  </span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.contractStart', 'Inicio')}</span>
                  <span>{formatDate(detail.contractStart)}</span>
                </div>
                <div>
                  <span className="detail-label">{t('partners.contractEnd', 'Fin')}</span>
                  <span>{formatDate(detail.contractEnd)}</span>
                </div>
              </div>

              <div className="detail-link">
                <span className="detail-label">{t('partners.landingLink', 'Link de la landing')}</span>
                <div className="detail-link-row">
                  <code>{`${LANDING_BASE}/${detail.code}`}</code>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => copyLink(detail.code)}
                  >
                    <Copy size={14} /> {t('partners.copyLink', 'Copiar')}
                  </button>
                </div>
              </div>

              <h3 className="detail-section-title">
                {t('partners.conversionsTitle', 'Conversiones y comisiones')}
              </h3>
              {detail.conversions && detail.conversions.length > 0 ? (
                <table className="partners-table">
                  <thead>
                    <tr>
                      <th>{t('partners.conversionDate', 'Fecha')}</th>
                      <th>{t('partners.conversionStage', 'Etapa')}</th>
                      <th>{t('partners.totalMRR', 'MRR')}</th>
                      <th>{t('partners.commissionAccrued', 'Comisión')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.conversions.map((c, i) => (
                      <tr key={c.id || i}>
                        <td>{formatDate(c.date)}</td>
                        <td>{c.stage}</td>
                        <td>{formatArs(c.mrr)}</td>
                        <td>{formatArs(c.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="detail-pending">
                  {t(
                    'partners.conversionsPending',
                    'El log de conversiones, comisión lifetime y próximo payout estarán disponibles cuando el commission calculator (NER-3081) esté desplegado.'
                  )}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setDetail(null)}>
                {t('common.close')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setDetail(null);
                  handleOpenModal(detail);
                }}
              >
                {t('common.edit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnersPage;
